import { useAddress } from '@hooks/use-address';
import { useBalance } from '@hooks/use-balance';
import { useTransaction } from '@hooks/use-transaction';
import { type CoinStruct } from '@models/coins.vault.model';
import { walletConnectModel } from '@models/wallet-connect.model';
import { Transaction, type TransactionObjectArgument } from '@mysten/sui/transactions';
import Button from '@ui/button';
import { numericFormat, percentage } from '@utils/index';
import { scaleDown } from '@utils/math';
import { suiCoinAddressBalanceAdd } from '@utils/sui-coin-address-balance-add';
import { BN, Decimal } from 'parrot-sui-sdk';
import { FC, useMemo } from 'react';
import { cetus } from 'parrot-vaults-sdk';
import { OpenapiClientParrotVault } from 'src/openapi/parrot-vault';
import { SUI_CLOCK_OBJECT_ID } from '@mysten/sui/utils';
import { cetusClmmSDK } from '@libs/cetus.client';
import { config } from '@config';
import { adjustForCoinSlippage, TransactionUtil } from '@cetusprotocol/cetus-sui-clmm-sdk';
import { type QuoteResponse } from '@7kprotocol/sdk-ts';
import { type MyVaultsStruct } from '@models/myvault.model';
import { K7Aggregator } from '@libs/7k.provider';
import { globalSettingModel } from '@models/global-setting.model';
import { useComputed } from 'foca';
import styles from './submit.module.scss';

interface OwnProps {
  myVault: MyVaultsStruct | undefined;
  vault: OpenapiClientParrotVault.GetVaultVaultsResponse['result'][number];
  coinA: CoinStruct;
  coinB: CoinStruct;
  tabValue: number;
  amountA: string;
  amountB: string;
  closeDialog(): void;
  exactIn: boolean;
  aggregatorRoute: QuoteResponse | undefined;
}
const DepositSubmit: FC<OwnProps> = ({
  amountA,
  amountB,
  tabValue,
  coinA,
  coinB,
  closeDialog,
  vault,
  exactIn,
  aggregatorRoute,
  myVault,
}) => {
  const slippage = useComputed(globalSettingModel.liquiditySlippage);

  const address = useAddress();
  const transact = useTransaction();

  const { balance: balanceA } = useBalance(coinA.coin_type);
  const { balance: balanceB } = useBalance(coinB.coin_type);

  const coinBalanceA = balanceA ? suiCoinAddressBalanceAdd(coinA.coin_type, balanceA) : '0';
  const coinBalanceB = balanceB ? suiCoinAddressBalanceAdd(coinB.coin_type, balanceB) : '0';

  const deposit = async () => {
    if (!address) return;

    let final_amount_a = amountA;
    let final_amount_b = amountB;

    if (tabValue === 2 && aggregatorRoute) {
      final_amount_a = new Decimal(final_amount_a)
        .sub(aggregatorRoute.swapAmountWithDecimal)
        .toString();
      final_amount_b = aggregatorRoute.returnAmountWithDecimal;
    } else if (tabValue === 3 && aggregatorRoute) {
      final_amount_b = new Decimal(final_amount_b)
        .sub(aggregatorRoute.swapAmountWithDecimal)
        .toString();
      final_amount_a = aggregatorRoute.returnAmountWithDecimal;
    }

    const slippageTolerance = percentage(Number(slippage));
    const { tokenMaxA, tokenMaxB } = adjustForCoinSlippage(
      {
        coinA: new BN(final_amount_a),
        coinB: new BN(final_amount_b),
      },
      slippageTolerance,
      false,
    );

    let _amount_a = '0';
    let _amount_b = '0';
    let _title = <></>;

    if (tabValue === 2) {
      _amount_a = scaleDown(amountA, coinA.decimals).toString();
      _title = (
        <>
          Deposit {numericFormat(_amount_a)} {coinA.symbol}
        </>
      );
    } else if (tabValue === 3) {
      _amount_b = scaleDown(amountB, coinB.decimals).toString();
      _title = (
        <>
          Deposit {numericFormat(_amount_b)} {coinB.symbol}
        </>
      );
    } else {
      _amount_a = scaleDown(amountA, coinA.decimals).toString();
      _amount_b = scaleDown(amountB, coinB.decimals).toString();
      _title = (
        <>
          Deposit {numericFormat(_amount_a)} {coinA.symbol} and {numericFormat(_amount_b)}{' '}
          {coinB.symbol}{' '}
        </>
      );
    }

    const ok = await transact({
      title: _title,
      successDescription: _title,
      fn: async () => {
        const _slippage = new Decimal(slippage).div(100).toNumber();
        let txb = new Transaction();
        const allCoinAsset = await cetusClmmSDK.getOwnerCoinAssets(address);
        let coinAInputs: TransactionObjectArgument;
        let coinBInputs: TransactionObjectArgument;

        if (tabValue === 2) {
          const primaryCoinAInputs = TransactionUtil.buildAddLiquidityFixTokenCoinInput(
            txb,
            !exactIn,
            amountA,
            _slippage,
            coinA.coin_type,
            allCoinAsset,
            false,
          );
          const [coinObject7k] = txb.splitCoins(primaryCoinAInputs.targetCoin, [
            Number(aggregatorRoute.swapAmountWithDecimal),
          ]);

          const { tx, coinOut } = await K7Aggregator.buildTransaction(
            aggregatorRoute!,
            _slippage.toString(), // 1%
            address,
            {
              tx: txb,
              coinIn: coinObject7k,
            },
          );
          txb = tx;

          coinAInputs = primaryCoinAInputs.targetCoin;
          coinBInputs = coinOut!;

          final_amount_a = tokenMaxA.toString();
        } else if (tabValue === 3) {
          const primaryCoinBInputs = TransactionUtil.buildAddLiquidityFixTokenCoinInput(
            txb,
            exactIn,
            amountB,
            _slippage,
            coinB.coin_type,
            allCoinAsset,
            false,
          );
          const [coinObject7k] = txb.splitCoins(primaryCoinBInputs.targetCoin, [
            Number(aggregatorRoute!.swapAmountWithDecimal),
          ]);

          const { tx, coinOut } = await K7Aggregator.buildTransaction(
            aggregatorRoute!,
            _slippage.toString(), // 1%
            address,
            {
              tx: txb,
              coinIn: coinObject7k,
            },
          );

          txb = tx;

          coinAInputs = coinOut!;
          coinBInputs = primaryCoinBInputs.targetCoin;

          final_amount_b = tokenMaxB.toString();
        } else {
          const primaryCoinAInputs = TransactionUtil.buildAddLiquidityFixTokenCoinInput(
            txb,
            !exactIn,
            amountA,
            _slippage,
            coinA.coin_type,
            allCoinAsset,
            false,
          );

          const primaryCoinBInputs = TransactionUtil.buildAddLiquidityFixTokenCoinInput(
            txb,
            exactIn,
            amountB,
            _slippage,
            coinB.coin_type,
            allCoinAsset,
            false,
          );
          coinAInputs = primaryCoinAInputs.targetCoin;
          coinBInputs = primaryCoinBInputs.targetCoin;
        }

        let vaultPosition: TransactionObjectArgument;
        if (myVault?.id) {
          vaultPosition = txb.object(myVault.id);
        } else {
          vaultPosition = cetus.openPosition(txb, {
            vault: vault.vault_id,
            imageUrl: 'https://gateway.irys.xyz/H6RiSi7PDvKaKCzgsLybW1Pdaq4pvakBHnTvghWD5Kad',
            manager: config.vaultsManager,
          });
        }

        const [coinALeft, coinBLeft] = cetus.deposit(txb, [coinA.coin_type, coinB.coin_type], {
          vault: vault.vault_id,
          rewarderManager: config.rewarderManager,
          manager: config.vaultsManager,
          clmmConfig: cetusClmmSDK.sdkOptions.clmm_pool.config!.global_config_id,
          clmmPool: vault.pool_id,
          vaultPosition: vaultPosition,
          coinA: coinAInputs,
          coinB: coinBInputs,
          amountALimit: txb.pure.u64(final_amount_a),
          amountBLimit: txb.pure.u64(final_amount_b),
          isFixedAmountA: exactIn,
          clock: SUI_CLOCK_OBJECT_ID,
        });

        if (!myVault?.id) {
          txb.transferObjects([vaultPosition], address);
        }
        txb.transferObjects([coinALeft!, coinBLeft!], address);

        return txb;
      },
    });
    if (ok.status) {
      closeDialog();
    }
  };

  const { disabled, text, clickEvent } = useMemo(() => {
    let disabled = false;
    let text = 'Deposit';
    let clickEvent = () => {};

    if (!address) {
      text = 'Connect Wallet';
      clickEvent = walletConnectModel.toggleDialog;
    } else if (
      (tabValue === 1 && (!amountA || !amountB)) ||
      (tabValue === 2 && !amountA) ||
      (tabValue === 3 && !amountB)
    ) {
      disabled = true;
    } else if (tabValue !== 3 && (!coinBalanceA || new Decimal(amountA).gt(coinBalanceA))) {
      text = `Insufficient ${coinA.symbol} balance`;
      disabled = true;
    } else if (tabValue !== 2 && (!coinBalanceB || new Decimal(amountB).gt(coinBalanceB))) {
      text = `Insufficient ${coinB.symbol} balance`;
      disabled = true;
    } else if ((tabValue === 2 || tabValue === 3) && !aggregatorRoute) {
      disabled = true;
    } else {
      clickEvent = deposit;
    }

    return {
      disabled,
      text,
      clickEvent,
    };
  }, [
    amountA,
    amountB,
    tabValue,
    address,
    coinBalanceA,
    coinBalanceB,
    coinA.symbol,
    coinB.symbol,
    walletConnectModel.toggleDialog,
    deposit,
  ]);

  return (
    <Button disabled={disabled} fullWidth onClick={clickEvent}>
      {text}
    </Button>
  );
};

export default DepositSubmit;
